import React, {useCallback, useMemo, useState} from 'react';
import {
	faMousePointer as MousePointerIcon,
	faTimes as TimesIcon,
} from '@fortawesome/pro-solid-svg-icons';
import {faUndo as CaretDownIcon} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Avatar, Menu} from '@mui/material';
import type {PopoverProps} from '@mui/material/Popover';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import {Chip, type ChipProps} from '@netx/core-components/Chip';
import {MenuItem} from '@netx/core-components/MenuItem';
import {type Theme} from '@netx/core-components/Theme';
import type {ReactAction as Action} from '@netx/core/bootstrap';
import {i18n} from '@netx/core/i18n';
import {useChangeStore, useGridStore, useSelectionStore} from '../../store';

const styles = (theme: Theme) =>
	createStyles({
		iconWrapper: {
			background: theme.palette.warning.main,
			borderRadius: '50%',
			width: 20,
			height: 20,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			'& svg': {
				color: theme.palette.background.default,
				width: '0.5em !important',
			},
		},
	});

export interface ChangesChipProps
	extends Omit<ChipProps, 'deleteIcon' | 'onClick' | 'onDelete'> {}

const ChangesChip: React.FC<WithStyles<typeof styles> & ChangesChipProps> = ({
	classes,
	disabled,
	label,
	...props
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);

	const chipRef = React.useRef<HTMLDivElement>(null);

	const changeStore = useChangeStore();
	const selectionStore = useSelectionStore();
	const gridStore = useGridStore();

	const actions = useMemo(
		() =>
			[
				{
					icon: MousePointerIcon,
					label: i18n.t('i18n.resetSelected'),
					cb: gridStore.resetSelectedChanges,
					enableTest: () => !!selectionStore.state.count,
				},
				{
					icon: TimesIcon,
					label: i18n.t('i18n.resetAll'),
					cb: gridStore.resetAllChanges,
				},
			].filter((action) => !action.enableTest || action.enableTest()),
		[
			gridStore.resetAllChanges,
			gridStore.resetSelectedChanges,
			selectionStore.state.count,
		],
	);

	const handleClose = useCallback<Required<PopoverProps>['onClose']>(
		(event: React.MouseEvent<HTMLElement, MouseEvent>, reason) => {
			setOpen(false);
		},
		[],
	);

	/** Menu exited */
	const handleExited = useCallback(() => setAnchorEl(null), []);

	/** Show options or execute the only option */
	const handleShowClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			event.stopPropagation();
			event.preventDefault();

			// Execute only option
			if (actions.length === 1) {
				actions[0].cb();
				return;
			}

			// Show options menu
			setAnchorEl(chipRef.current);
			setOpen(true);
		},
		[actions],
	);

	const handleActionClick = useCallback(
		(action: Action) =>
			(
				event: React.MouseEvent<HTMLAnchorElement | HTMLLIElement, MouseEvent>,
			) => {
				event.stopPropagation();
				event.preventDefault();

				setOpen(false);
				action.cb();
			},
		[],
	);

	return useMemo(
		() => (
			<>
				<Chip
					avatar={
						<Avatar>
							{changeStore.state.count > 99 ? '99+' : changeStore.state.count}
						</Avatar>
					}
					meshAvatar
					variant="outlined"
					{...props}
					deleteIcon={
						<div className={classes.iconWrapper}>
							<FontAwesomeIcon icon={CaretDownIcon} />
						</div>
					}
					disabled={disabled || !changeStore.state.count}
					label={label || i18n.t('i18n.changed')}
					onDelete={changeStore.state.count ? handleShowClick : undefined}
					onClick={handleShowClick}
					ref={chipRef}
					color="warning"
				/>
				{anchorEl ? (
					<Menu
						anchorEl={anchorEl}
						onClose={handleClose}
						open={open}
						TransitionProps={{
							onExit: handleExited,
						}}
					>
						{actions.map((action) => (
							<MenuItem
								key={`row-action-${action.label}`}
								icon={action.icon}
								onClick={handleActionClick(action as any)}
								text={action.label}
							/>
						))}
					</Menu>
				) : null}
			</>
		),
		[
			actions,
			anchorEl,
			open,

			disabled,
			label,

			handleClose,
			handleExited,
			handleActionClick,
			handleShowClick,

			changeStore.state.count,
		],
	);
};

export default withStyles(styles)(ChangesChip as any);
